import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  adminGetHost,
  adminHostDashboard,
  adminHostProperties,
  adminHostBookings,
  adminHostDocuments,
  adminHostPayouts,
  adminSetHostStatus,
  adminCreateHostProperty,
  adminUpdateKycDoc,
  adminCreatePayout,
  adminPayoutAction,
  adminHostBankAccount,
  adminUpdateHostBankAccountStatus,
} from '../api/adminHosts';

const Pill = ({ label, value }) => (
  <div className="px-3 py-2 rounded-lg bg-slate-100 text-slate-800 text-sm font-semibold">
    {label}: {value}
  </div>
);

const bankStatusClass = (status) => {
  const normalized = (status || 'PENDING').toUpperCase();
  if (normalized === 'VERIFIED') return 'bg-success';
  if (normalized === 'REJECTED') return 'bg-danger';
  if (normalized === 'NOT_SUBMITTED') return 'bg-secondary';
  return 'bg-warning text-dark';
};

const BankStatusBadge = ({ status }) => {
  const value = (status || 'PENDING').toUpperCase();
  return <span className={`badge ${bankStatusClass(value)}`}>{value}</span>;
};

const AdminHostDetail = ({ token }) => {
  const { id } = useParams();
  const [host, setHost] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [props, setProps] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [docs, setDocs] = useState([]);
  const [docNotes, setDocNotes] = useState({});
  const [payouts, setPayouts] = useState([]);
  const [bankAccount, setBankAccount] = useState(null);
  const [bankError, setBankError] = useState('');
  const [bankRejectionReason, setBankRejectionReason] = useState('');
  const [bankUpdating, setBankUpdating] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [newProperty, setNewProperty] = useState({ title: '', city: '', base_price_per_night: 0, status: 'draft' });
  const [newPayout, setNewPayout] = useState({ amount: '', currency: 'INR' });

  const load = async () => {
    setLoading(true);
    setError('');
    setBankError('');
    try {
      const [h, dash, pr, bk, dc, po] = await Promise.all([
        adminGetHost(token, id),
        adminHostDashboard(token, id),
        adminHostProperties(token, id),
        adminHostBookings(token, id),
        adminHostDocuments(token, id),
        adminHostPayouts(token, id),
      ]);
      setHost(h);
      setDashboard(dash);
      setProps(pr);
      setBookings(bk);
      setDocs(dc);
      setDocNotes(
        (dc || []).reduce((acc, d) => {
          acc[d.id] = d.admin_comment || '';
          return acc;
        }, {})
      );
      setPayouts(po);
      try {
        const account = await adminHostBankAccount(token, id);
        setBankAccount(account);
        setBankRejectionReason(account?.rejection_reason || '');
      } catch (err) {
        if (String(err?.message || '').toLowerCase().includes('not found')) {
          setBankAccount(null);
          setBankRejectionReason('');
        } else {
          setBankAccount(null);
          setBankError(err.message || 'Failed to load bank account');
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to load host');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const changeStatus = async (status, reason) => {
    try {
      await adminSetHostStatus(token, id, status, { reason });
      await load();
    } catch (err) {
      setError(err.message || 'Status update failed');
    }
  };

  const addProperty = async () => {
    try {
      await adminCreateHostProperty(token, id, newProperty);
      setNewProperty({ title: '', city: '', base_price_per_night: 0, status: 'draft' });
      await load();
    } catch (err) {
      setError(err.message || 'Create property failed');
    }
  };

  const updateDocStatus = async (docId, status) => {
    try {
      await adminUpdateKycDoc(token, docId, { status, admin_comment: docNotes[docId] || '' });
      await load();
    } catch (err) {
      setError(err.message || 'Document update failed');
    }
  };

  const createPayout = async () => {
    await adminCreatePayout(token, id, newPayout);
    setNewPayout({ amount: '', currency: 'INR' });
    await load();
  };

  const payoutAction = async (payoutId, action) => {
    await adminPayoutAction(token, payoutId, action);
    await load();
  };

  const updateBankStatus = async (status) => {
    if (!bankAccount) return;
    if (status === 'REJECTED' && !bankRejectionReason.trim()) {
      setBankError('Rejection reason is required');
      return;
    }
    setBankUpdating(true);
    setBankError('');
    try {
      const updated = await adminUpdateHostBankAccountStatus(
        token,
        id,
        status,
        status === 'REJECTED' ? bankRejectionReason.trim() : ''
      );
      setBankAccount(updated);
      if (status !== 'REJECTED') {
        setBankRejectionReason('');
      }
    } catch (err) {
      setBankError(err.message || 'Failed to update bank account');
    } finally {
      setBankUpdating(false);
    }
  };

  if (loading && !host) return <div className="p-4">Loading...</div>;
  if (error) return <div className="p-4 text-red-600">{error}</div>;
  if (!host) return null;
  const bankStatus = bankAccount ? bankAccount.status : 'NOT_SUBMITTED';

  return (
    <div className="p-4 space-y-4">
      <div className="d-flex justify-content-between align-items-center">
        <div>
          <h4 className="mb-0">{host.full_name}</h4>
          <small className="text-muted">{host.phone} Â· {host.email}</small>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-success btn-sm" onClick={() => changeStatus('APPROVED')}>Approve</button>
          <button className="btn btn-warning btn-sm" onClick={() => changeStatus('REJECTED')}>Reject</button>
          <button className="btn btn-danger btn-sm" onClick={() => changeStatus('BLOCKED')}>Block</button>
          <button className="btn btn-secondary btn-sm" onClick={() => changeStatus('APPROVED')}>Unblock</button>
        </div>
      </div>

      {dashboard && (
        <div className="d-flex gap-2 flex-wrap">
          <Pill label="Properties" value={dashboard.properties?.reduce((a, b) => a + (b.count || 0), 0) || 0} />
          <Pill label="Bookings" value={dashboard.bookings?.reduce((a, b) => a + (b.count || 0), 0) || 0} />
          <Pill label="Payouts" value={dashboard.payouts?.reduce((a, b) => a + (b.count || 0), 0) || 0} />
          <Pill label="Docs" value={dashboard.documents?.reduce((a, b) => a + (b.count || 0), 0) || 0} />
        </div>
      )}

      <div className="card">
        <div className="card-header">Properties</div>
        <div className="card-body">
          <div className="mb-3 d-flex gap-2">
            <input
              className="form-control"
              placeholder="Title"
              value={newProperty.title}
              onChange={(e) => setNewProperty((f) => ({ ...f, title: e.target.value }))}
            />
            <input
              className="form-control"
              placeholder="City"
              value={newProperty.city}
              onChange={(e) => setNewProperty((f) => ({ ...f, city: e.target.value }))}
            />
            <input
              className="form-control"
              placeholder="Price"
              type="number"
              value={newProperty.base_price_per_night}
              onChange={(e) => setNewProperty((f) => ({ ...f, base_price_per_night: Number(e.target.value) }))}
            />
            <button className="btn btn-primary" onClick={addProperty}>Add</button>
          </div>
          <table className="table table-sm">
            <thead>
              <tr>
                <th>Title</th>
                <th>City</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {props.map((p) => (
                <tr key={p.id}>
                  <td>{p.title}</td>
                  <td>{p.city}</td>
                  <td>{p.status}</td>
                </tr>
              ))}
              {props.length === 0 && (
                <tr>
                  <td colSpan="3" className="text-center text-muted">No properties</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <div className="card-header">Bookings</div>
        <div className="card-body">
          <table className="table table-sm">
            <thead>
              <tr>
                <th>Code</th>
                <th>Status</th>
                <th>Payment</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((b) => (
                <tr key={b.id}>
                  <td>{b.booking_code}</td>
                  <td>{b.booking_status}</td>
                  <td>{b.payment_status}</td>
                  <td>{b.currency} {b.total_amount}</td>
                </tr>
              ))}
              {bookings.length === 0 && (
                <tr>
                  <td colSpan="4" className="text-center text-muted">No bookings</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <div className="card-header">Documents</div>
        <div className="card-body">
          <table className="table table-sm align-middle">
            <thead>
              <tr>
                <th>Type</th>
                <th>Number</th>
                <th>Status</th>
                <th>Uploaded</th>
                <th>File</th>
                <th>Admin comment</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.id}>
                  <td>{d.doc_type || d.document_type}</td>
                  <td>{d.doc_number || d.document_number || 'â€”'}</td>
                  <td>
                    <span className="badge bg-light text-dark">{d.status}</span>
                  </td>
                  <td>{d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString() : 'â€”'}</td>
                  <td>{d.file_url ? <a href={d.file_url.startsWith('http') ? d.file_url : `${process.env.REACT_APP_API_BASE_URL || ''}${d.file_url}`} target="_blank" rel="noreferrer">View</a> : 'â€”'}</td>
                  <td style={{ minWidth: '160px' }}>
                    <input
                      className="form-control form-control-sm"
                      placeholder="Add comment"
                      value={docNotes[d.id] || ''}
                      onChange={(e) => setDocNotes((prev) => ({ ...prev, [d.id]: e.target.value }))}
                    />
                  </td>
                  <td className="d-flex gap-2">
                    <button className="btn btn-success btn-sm" onClick={() => updateDocStatus(d.id, 'APPROVED')}>Approve</button>
                    <button className="btn btn-danger btn-sm" onClick={() => updateDocStatus(d.id, 'REJECTED')}>Reject</button>
                  </td>
                </tr>
              ))}
              {docs.length === 0 && (
                <tr>
                  <td colSpan="7" className="text-center text-muted">No documents</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <span>Bank Account</span>
          <BankStatusBadge status={bankStatus} />
        </div>
        <div className="card-body">
          {bankError && (
            <div className="alert alert-danger py-2">{bankError}</div>
          )}
          {!bankAccount ? (
            <div className="text-muted">No bank account submitted.</div>
          ) : (
            <>
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="text-muted small">Account holder</div>
                  <div className="fw-semibold">{bankAccount.account_holder_name}</div>
                </div>
                <div className="col-md-6">
                  <div className="text-muted small">Bank name</div>
                  <div className="fw-semibold">{bankAccount.bank_name}</div>
                </div>
                <div className="col-md-6">
                  <div className="text-muted small">Account number</div>
                  <div className="fw-semibold">{bankAccount.account_number_masked}</div>
                </div>
                <div className="col-md-6">
                  <div className="text-muted small">IFSC</div>
                  <div className="fw-semibold">{bankAccount.ifsc}</div>
                </div>
              </div>

              {bankAccount.rejection_reason && (
                <div className="mt-3 small text-danger">
                  Rejection reason: {bankAccount.rejection_reason}
                </div>
              )}

              <div className="mt-4 d-flex flex-wrap align-items-end gap-2">
                <div className="flex-fill">
                  <label className="form-label mb-1">Rejection reason</label>
                  <input
                    className="form-control"
                    placeholder="Reason required when rejecting"
                    value={bankRejectionReason}
                    onChange={(e) => setBankRejectionReason(e.target.value)}
                  />
                </div>
                <button
                  className="btn btn-success btn-sm"
                  onClick={() => updateBankStatus('VERIFIED')}
                  disabled={bankUpdating}
                >
                  Approve
                </button>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => updateBankStatus('REJECTED')}
                  disabled={bankUpdating}
                >
                  Reject
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header">Payouts</div>
        <div className="card-body">
          <div className="mb-3 d-flex gap-2">
            <input
              className="form-control"
              placeholder="Amount"
              type="number"
              value={newPayout.amount}
              onChange={(e) => setNewPayout((f) => ({ ...f, amount: e.target.value }))}
            />
            <input
              className="form-control"
              placeholder="Currency"
              value={newPayout.currency}
              onChange={(e) => setNewPayout((f) => ({ ...f, currency: e.target.value }))}
            />
            <button className="btn btn-primary" onClick={createPayout}>Create payout</button>
          </div>
          <table className="table table-sm">
            <thead>
              <tr>
                <th>Amount</th>
                <th>Status</th>
                <th>Notes</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {payouts.map((p) => (
                <tr key={p.id}>
                  <td>{p.currency} {p.amount}</td>
                  <td>{p.status}</td>
                  <td>{p.admin_notes}</td>
                  <td className="d-flex gap-2">
                    <button className="btn btn-success btn-sm" onClick={() => payoutAction(p.id, 'APPROVE')}>Approve</button>
                    <button className="btn btn-warning btn-sm" onClick={() => payoutAction(p.id, 'DISAPPROVE')}>Disapprove</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => payoutAction(p.id, 'PAID')}>Mark paid</button>
                  </td>
                </tr>
              ))}
              {payouts.length === 0 && (
                <tr>
                  <td colSpan="4" className="text-center text-muted">No payouts</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminHostDetail;

